import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ReactNode } from 'react';
import NewCollapsibleTable from './drop-order-details';
import { formatDateWithTime, formatTime } from 'utils/date-helpers';
import { formatPrice } from 'utils/price-helpers';
import { DELIVERY_METHOD } from '../constants/method';
import { ResponseOrderDto } from '../types/dtos';

type InfoBoxProps = {
    title: string;
    children?: ReactNode;
};

type DetailsViewProps = {
    order: ResponseOrderDto;
    isAdmin: boolean;
    handleBack?: () => void;
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.common.white,
        color: theme.palette.common.black,
        fontWeight: 'bold',
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.background.paper,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

export default function DeatilsOrderView({ handleBack, order, isAdmin }: DetailsViewProps) {
    const { t } = useTranslation();

    return (
        <>
            <Stack sx={{ flexDirection: { xs: 'column', sm: 'column', md: 'row' } }}>
                <Stack sx={{ flex: 7, paddingRight: 5, direction: 'column' }}>
                    <NewCollapsibleTable
                        order={order}
                        isAdmin={isAdmin}
                        handleBack={handleBack}
                        columnNames={[
                            t('ORDER_VIEW.PRODUCT'),
                            t('ORDER_VIEW.QUANTITY'),
                            t('ORDER_VIEW.PRICE'),
                            t('ORDER_VIEW.TOTAL_PRICE'),
                        ]}
                    />
                    <FeeProductsTable order={order} isAdmin={isAdmin} handleBack={handleBack} />
                </Stack>
                <Stack sx={{ flex: 3, gap: 4, direction: 'column' }}>
                    <InfoBox title={t('ORDER_VIEW.ORDER_INFO')}>
                        <Typography>
                            {t('ORDER_VIEW.ORDER_ID')}: {order.id}
                        </Typography>
                        <Typography>
                            {t('ORDER_VIEW.ORDER_DATE')}:{formatDateWithTime(order.createdAt)}
                        </Typography>
                        <Stack direction="row" sx={{ flex: 1, alignItems: 'center' }}>
                            <Typography>
                                {t('ORDER_VIEW.ORDER_STATUS')}:{order.orderStatus}
                            </Typography>
                        </Stack>
                        <Typography>
                            {t('ORDER_VIEW.ORDER_TOTAL')}: {formatPrice(order.totalPrice)}
                        </Typography>
                    </InfoBox>
                    <InfoBox title={t('ORDER_VIEW.CUSTOMER')}>
                        <Typography>
                            {t('ORDER_VIEW.CUSTOMER_FULL_NAME')}: {order.endUserFullName}
                        </Typography>
                        <Typography>
                            {t('ORDER_VIEW.STREET')}: {order.streetAddress}
                        </Typography>
                        <Typography>
                            {' '}
                            {t('ORDER_VIEW.APARTMENT')}:{order.apartment}
                        </Typography>
                        <Typography>
                            {t('ORDER_VIEW.FLOOR')}: {order.floor}
                        </Typography>
                        <Typography>
                            {t('ORDER_VIEW.INTERCOM')}:{order.intercom}
                        </Typography>
                    </InfoBox>
                    <InfoBox title={t('ORDER_VIEW.INFO')}>
                        <Typography>
                            {t('ORDER_VIEW.PAYMENT_METHOD')}: {t(`TYPES.${order.paymentMethod}`)}
                        </Typography>
                        <Typography>
                            {' '}
                            {t('ORDER_VIEW.DELIVEY_METHOD')}: {t(`TYPES.${order.deliveryMethod}`)}
                        </Typography>
                        <Typography>{`${order.deliveryMethod === DELIVERY_METHOD.DELIVERY ? t('TYPES.DELIVERY') : t('TYPES.PICKUP')} (VREME): ${order.deliveryTime ? formatTime(order.deliveryTime) : ''}`}</Typography>
                    </InfoBox>
                </Stack>
            </Stack>
        </>
    );
}

const FeeProductsTable = ({ order }: DetailsViewProps) => {
    const { t } = useTranslation();

    return (
        <TableContainer component={Paper}>
            <Table aria-label="customized table">
                <TableHead>
                    <TableRow>
                        <StyledTableCell>{t('ORDER_VIEW.PAYMENT_DETAILS')}</StyledTableCell>
                        <StyledTableCell align="right">{t('ORDER_VIEW.TOTAL')}</StyledTableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <StyledTableRow>
                        <StyledTableCell component="th" scope="order">
                            {t('TYPES.DELIVERY')}
                        </StyledTableCell>
                        <StyledTableCell align="right">{formatPrice(order.deliveryFee)}</StyledTableCell>
                    </StyledTableRow>
                    <StyledTableRow>
                        <StyledTableCell component="th" scope="order">
                            {t('ORDER_VIEW.TOTAL')}
                        </StyledTableCell>
                        <StyledTableCell align="right">{formatPrice(order.totalPrice)}</StyledTableCell>
                    </StyledTableRow>
                    <StyledTableRow>
                        <StyledTableCell sx={{ fontWeight: 600 }} component="th" scope="order">
                            {t('ORDER_VIEW.PAYMENT_TOTAL')}
                        </StyledTableCell>
                        <StyledTableCell sx={{ color: 'red', fontWeight: 600 }} align="right">
                            {formatPrice(Number(order.totalPrice) + Number(order.deliveryFee))}
                        </StyledTableCell>
                    </StyledTableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const InfoBox = ({ title, children }: InfoBoxProps) => {
    return (
        <Box
            sx={{
                background: '#fff',
                alignContent: 'center',
                borderRadius: 4,
                padding: 2,
            }}
            borderRadius={4}
        >
            <Typography variant="subtitle1" fontWeight={'bold'} marginBottom={3}>
                {title}
            </Typography>
            {children}
        </Box>
    );
};
